/**
 * Rate Limiter Utility
 * Handles API rate limiting based on free tier constraints
 */

class RateLimiter {
  constructor(name, config) {
    this.name = name;
    this.maxPerDay = config.maxPerDay;
    this.maxPerMinute = config.maxPerMinute || Math.floor(config.maxPerDay / (24 * 60));
    this.maxPerSecond = config.maxPerSecond || Math.floor(this.maxPerMinute / 60);
    
    // Tracking
    this.dailyCount = 0;
    this.minuteCount = 0;
    this.secondCount = 0;
    
    // Timestamps
    this.dayStart = Date.now();
    this.minuteStart = Date.now();
    this.secondStart = Date.now();
    
    // Queue for requests
    this.queue = [];
    this.processing = false;
    
    // Load from localStorage
    this.loadState();
  }

  /**
   * Load rate limit state from localStorage
   */
  loadState() {
    try {
      const stored = localStorage.getItem(`rateLimiter_${this.name}`);
      if (stored) {
        const state = JSON.parse(stored);
        const now = Date.now();
        
        // Reset if day has passed
        if (now - state.dayStart > 24 * 60 * 60 * 1000) {
          this.resetDaily();
        } else {
          this.dailyCount = state.dailyCount || 0;
          this.dayStart = state.dayStart || now;
        }
        
        // Reset if minute has passed
        if (now - state.minuteStart > 60 * 1000) {
          this.resetMinute();
        } else {
          this.minuteCount = state.minuteCount || 0;
          this.minuteStart = state.minuteStart || now;
        }
      }
    } catch (error) {
      console.warn(`Error loading rate limiter state for ${this.name}:`, error);
    }
  }

  /**
   * Save rate limit state to localStorage
   */
  saveState() {
    try {
      const state = {
        dailyCount: this.dailyCount,
        minuteCount: this.minuteCount,
        dayStart: this.dayStart,
        minuteStart: this.minuteStart
      };
      localStorage.setItem(`rateLimiter_${this.name}`, JSON.stringify(state));
    } catch (error) {
      console.warn(`Error saving rate limiter state for ${this.name}:`, error);
    }
  }

  /**
   * Reset daily counter
   */
  resetDaily() {
    this.dailyCount = 0;
    this.dayStart = Date.now();
    this.saveState();
  }

  /**
   * Reset minute counter
   */
  resetMinute() {
    this.minuteCount = 0;
    this.minuteStart = Date.now();
  }

  /**
   * Reset second counter
   */
  resetSecond() {
    this.secondCount = 0;
    this.secondStart = Date.now();
  }

  /**
   * Check if we're within rate limits
   */
  canMakeRequest() {
    const now = Date.now();
    
    // Check and reset counters if time windows have passed
    if (now - this.dayStart > 24 * 60 * 60 * 1000) {
      this.resetDaily();
    }
    if (now - this.minuteStart > 60 * 1000) {
      this.resetMinute();
    }
    if (now - this.secondStart > 1000) {
      this.resetSecond();
    }
    
    // Check limits
    if (this.dailyCount >= this.maxPerDay) {
      const timeUntilReset = (this.dayStart + 24 * 60 * 60 * 1000) - now;
      console.warn(`${this.name}: Daily limit reached (${this.maxPerDay}/day). Resets in ${Math.ceil(timeUntilReset / 1000 / 60)} minutes`);
      return false;
    }
    
    if (this.minuteCount >= this.maxPerMinute) {
      const timeUntilReset = (this.minuteStart + 60 * 1000) - now;
      // Only log warning once per minute to avoid spam
      if (this.minuteCount === this.maxPerMinute) {
        console.warn(`${this.name}: Per-minute limit reached (${this.maxPerMinute}/min). Resets in ${Math.ceil(timeUntilReset / 1000)} seconds`);
      }
      return false;
    }
    
    if (this.maxPerSecond && this.secondCount >= this.maxPerSecond) {
      const timeUntilReset = (this.secondStart + 1000) - now;
      // Only log warning once per second to avoid spam
      if (this.secondCount === this.maxPerSecond) {
        console.warn(`${this.name}: Per-second limit reached (${this.maxPerSecond}/sec). Resets in ${Math.ceil(timeUntilReset)} ms`);
      }
      return false;
    }
    
    return true;
  }

  /**
   * Increment request counters
   */
  recordRequest() {
    this.dailyCount++;
    this.minuteCount++;
    this.secondCount++;
    this.saveState();
  }

  /**
   * Get current usage statistics
   */
  getStats() {
    const now = Date.now();
    return {
      name: this.name,
      daily: {
        used: this.dailyCount,
        limit: this.maxPerDay,
        remaining: Math.max(0, this.maxPerDay - this.dailyCount),
        percentUsed: (this.dailyCount / this.maxPerDay) * 100,
        resetsIn: Math.ceil((this.dayStart + 24 * 60 * 60 * 1000 - now) / 1000 / 60) // minutes
      },
      minute: {
        used: this.minuteCount,
        limit: this.maxPerMinute,
        remaining: Math.max(0, this.maxPerMinute - this.minuteCount),
        resetsIn: Math.ceil((this.minuteStart + 60 * 1000 - now) / 1000) // seconds
      }
    };
  }

  /**
   * Execute a request with rate limiting
   * @param {Function} requestFn - Async function to execute
   * @returns {Promise} Result of the request
   */
  async executeRequest(requestFn) {
    // Check if we can make the request
    if (!this.canMakeRequest()) {
      const stats = this.getStats();
      throw new Error(
        `Rate limit exceeded for ${this.name}. ` +
        `Daily: ${stats.daily.used}/${stats.daily.limit} (resets in ${stats.daily.resetsIn}min). ` +
        `Per minute: ${stats.minute.used}/${stats.minute.limit}.`
      );
    }

    try {
      // Record the request
      this.recordRequest();
      
      // Execute the request
      const result = await requestFn();
      
      return result;
    } catch (error) {
      // If rate limit error from API, update our counters
      if (error.response?.status === 429) {
        console.error(`${this.name}: API returned 429 (rate limited)`);
      }
      throw error;
    }
  }

  /**
   * Add request to queue and process
   */
  async queueRequest(requestFn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFn, resolve, reject });
      this.processQueue();
    });
  }

  /**
   * Process queued requests with rate limiting
   */
  async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const { requestFn, resolve, reject } = this.queue[0];

      if (!this.canMakeRequest()) {
        // Wait a bit before trying again
        await new Promise(r => setTimeout(r, 1000));
        continue;
      }

      // Remove from queue
      this.queue.shift();

      try {
        const result = await this.executeRequest(requestFn);
        resolve(result);
      } catch (error) {
        reject(error);
      }

      // Small delay between requests
      if (this.queue.length > 0) {
        await new Promise(r => setTimeout(r, 100));
      }
    }

    this.processing = false;
  }
}

// Create rate limiters for each API
export const rateLimiters = {
  // CoinGlass v4 Free Tier: 500 requests/day, conservative per-minute limit
  coinglass: new RateLimiter('CoinGlass', {
    maxPerDay: 500,
    maxPerMinute: 5,  // Reduced from 10 to be more conservative
    maxPerSecond: 1
  }),

  // Etherscan Free Tier: 100,000 requests/day, conservative per-minute limit
  etherscan: new RateLimiter('Etherscan', {
    maxPerDay: 100000,
    maxPerMinute: 200,  // Reduced from 300 to be more conservative
    maxPerSecond: 3     // Reduced from 5 to be more conservative
  }),

  // Morpho API: Official Morpho Blue API (generous limits, 10k/day conservative)
  morpho: new RateLimiter('Morpho', {
    maxPerDay: 10000,
    maxPerMinute: 100,
    maxPerSecond: 10
  }),

  // CoinGecko Free API: 10-50 calls/minute (conservative: 30/min, 5k/day)
  coingecko: new RateLimiter('CoinGecko', {
    maxPerDay: 5000,
    maxPerMinute: 30,
    maxPerSecond: 1
  }),

  // DefiLlama: No official limit, but be respectful (10k/day, 100/min)
  defillama: new RateLimiter('DefiLlama', {
    maxPerDay: 10000,
    maxPerMinute: 100,
    maxPerSecond: 10
  })
};

/**
 * Get all rate limiter statistics
 */
export function getAllRateLimiterStats() {
  return Object.keys(rateLimiters).map(key => rateLimiters[key].getStats());
}

/**
 * Reset all rate limiters (useful for testing)
 */
export function resetAllRateLimiters() {
  Object.values(rateLimiters).forEach(limiter => {
    limiter.resetDaily();
    limiter.resetMinute();
    limiter.resetSecond();
  });
}

/**
 * Check if any rate limiter is near its limit
 * @param {number} threshold - Percentage threshold (default: 80)
 */
export function checkRateLimitWarnings(threshold = 80) {
  const warnings = [];
  
  Object.values(rateLimiters).forEach(limiter => {
    const stats = limiter.getStats();
    
    if (stats.daily.percentUsed >= threshold) {
      warnings.push({
        api: stats.name,
        type: 'daily',
        percentUsed: stats.daily.percentUsed.toFixed(1),
        remaining: stats.daily.remaining,
        resetsIn: stats.daily.resetsIn
      });
    }
  });
  
  return warnings;
}

export default RateLimiter;

